import React, {useRef, useId} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useStyleConfig} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideCard from './partials/promo-slide-card'
import {remCalc} from '../../../theme/_utils'
import PromoSliderIcons from './promo-slider-icon-arrow'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

export const PromoSlider = ({heading, sliderGroup, uid}) => {
    const generatedId = useId().replace(/:/g, '')
    const sliderId = uid || generatedId
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('PromoSlider')
    const swiperRef = useRef(null)
    const prevButtonRef = useRef(null)
    const nextButtonRef = useRef(null)
    const navigationDirection = useRef(null)
    const slidesToView = {
        base: 1,
        md: 1.5,
        lg: 2.75,
        xl: 2.75,
        xxl: 2.75,
    }

    if (!sliderGroup || sliderGroup?.length === 0) return null

    // swiperjs doesn't work correctly with fewer than 3 slides when loop and centeredSlides are enabled
    // I add duplicates and hide the bullets from the fake slide
    const slides = [...sliderGroup]
    const needsFakeSlides = sliderGroup.length <= 3
    const allSlides = needsFakeSlides
        ? [...slides, ...slides.map((slide) => ({...slide, isFake: true}))]
        : slides

    return (
        <Box
            __css={{...swiperStyles, ...styles.container}}
            data-testid="promo-slider-container"
            id={`promo-slider-image-${sliderId}`}
        >
            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            <Box position="relative">
                <HideOnMobile>
                    <Box
                        ref={prevButtonRef}
                        className={`swiper-button-prev swiper-button-prev-${sliderId}`}
                        aria-label="swiper button prev icon"
                        tabIndex={0}
                        onClick={() => {
                            navigationDirection.current = 'prev'
                            swiperRef.current?.slidePrev()
                            if (prevButtonRef.current) {
                                prevButtonRef.current.blur()
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                navigationDirection.current = 'prev'
                                swiperRef.current?.slidePrev()
                                if (prevButtonRef.current) {
                                    prevButtonRef.current.blur()
                                }
                            }
                        }}
                        role="button"
                        {...styles.heroLeftRightArrowsContainer}
                        top={{base: '50%', md: 'calc(50% - 60px)', lg: '50%'}}
                        left={{base: '4%', md: '18%', lg: '22%', xl: '26%'}}
                    >
                        <PromoSliderIcons.left
                            {...styles.heroLeftRightArrows}
                            boxSize={remCalc(16)}
                        />
                    </Box>
                </HideOnMobile>
                <Swiper
                    modules={[Navigation, Pagination, Keyboard]}
                    navigation={{
                        prevEl: `.swiper-button-prev-${sliderId}`,
                        nextEl: `.swiper-button-next-${sliderId}`,
                    }}
                    pagination={{
                        clickable: true,
                        el: `#promo-slider-image-${sliderId} .promo-slider-pagination`,
                        renderBullet: function (index, className) {
                            // only show bullets for the original slide count
                            if (index < slides.length) {
                                return `<span class="${className}" tabindex="-1"></span>`
                            }
                            return ''
                        },
                    }}
                    keyboard={{
                        enabled: true,
                        onlyInViewport: false,
                        pageUpDown: false,
                    }}
                    // mousewheel={true}
                    loop={true}
                    centeredSlides={true}
                    tabIndex={-1}
                    onInit={(swiper) => {
                        swiperRef.current = swiper

                        const allSlideLinks = document.querySelectorAll(
                            `#promo-slider-image-${sliderId} .swiper-slide a`,
                        )
                        allSlideLinks.forEach((link, idx) => {
                            if (idx === swiper.activeIndex) {
                                link.setAttribute('tabindex', '0')
                            } else {
                                link.setAttribute('tabindex', '-1')
                            }
                        })
                    }}
                    onSlideChange={(swiper) => {
                        const allSlideLinks = document.querySelectorAll(
                            `#promo-slider-image-${sliderId} .swiper-slide a`,
                        )
                        allSlideLinks.forEach((link, idx) => {
                            if (idx === swiper.activeIndex) {
                                link.setAttribute('tabindex', '0')
                            } else {
                                link.setAttribute('tabindex', '-1')
                            }
                        })

                        if (navigationDirection.current) {
                            setTimeout(() => {
                                const targetSlide = swiper.slides[swiper.activeIndex]
                                if (targetSlide) {
                                    const focusableElement =
                                        targetSlide.querySelector('a[tabindex="0"]')
                                    if (focusableElement) {
                                        focusableElement.focus()
                                    }
                                }
                                navigationDirection.current = null
                            }, 300)
                        }

                        if (!needsFakeSlides) return
                        // calculate which bullet point should be active
                        const realIndex = swiper.realIndex % slides.length

                        // here we need to include the id in the selector because there can be multiple promo sliders on the page
                        const bullets = document.querySelectorAll(
                            `#promo-slider-image-${sliderId} .swiper-pagination-bullet`,
                        )
                        bullets.forEach((bullet, idx) => {
                            if (idx === realIndex) {
                                bullet.classList.add('swiper-pagination-bullet-active')
                            } else {
                                bullet.classList.remove('swiper-pagination-bullet-active')
                            }
                        })
                    }}
                    breakpoints={{
                        0: {
                            slidesPerView: slidesToView.base,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        768: {
                            slidesPerView: slidesToView.md,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        1024: {
                            slidesPerView: slidesToView.lg,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        1200: {
                            slidesPerView: slidesToView.xl,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        1400: {
                            slidesPerView: slidesToView.xxl,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                    }}
                >
                    {allSlides.map((slider, index) => (
                        <SwiperSlide key={index} data-fake={slider.isFake ? 'true' : 'false'}>
                            <PromoSlideCard
                                title={slider?.text_block?.title}
                                bodyCopy={slider?.text_block?.body_copy}
                                button={slider?.text_block?.button}
                                image={slider?.image}
                                url={slider?.media?.url}
                                alt={slider?.media?.description}
                                tabIndex={-1}
                            />
                        </SwiperSlide>
                    ))}

                    <Box className="promo-slider-pagination" />
                </Swiper>
                <HideOnMobile>
                    <Box
                        ref={nextButtonRef}
                        className={`swiper-button-next swiper-button-next-${sliderId}`}
                        aria-label="swiper button next icon"
                        tabIndex={0}
                        onClick={() => {
                            navigationDirection.current = 'next'
                            swiperRef.current?.slideNext()
                            if (nextButtonRef.current) {
                                nextButtonRef.current.blur()
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                navigationDirection.current = 'next'
                                swiperRef.current?.slideNext()
                                if (nextButtonRef.current) {
                                    nextButtonRef.current.blur()
                                }
                            }
                        }}
                        role="button"
                        {...styles.heroLeftRightArrowsContainer}
                        top={{base: '50%', md: 'calc(50% - 60px)', lg: '50%'}}
                        right={{base: '4%', md: '18%', lg: '22%', xl: '26%'}}
                    >
                        <PromoSliderIcons.right
                            {...styles.heroLeftRightArrows}
                            boxSize={remCalc(16)}
                        />
                    </Box>
                </HideOnMobile>
            </Box>
        </Box>
    )
}

PromoSlider.propTypes = {
    heading: PropTypes.string,
    uid: PropTypes.string,
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                title: PropTypes.string,
                body_copy: PropTypes.string,
                cta: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
                is_external_url: PropTypes.bool,
            }),
            image: PropTypes.object,
            media: PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string,
            }),
        }),
    ),
}
